import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Popover,
  Typography,
  Rating,
  TextField,
  Button,
  CircularProgress,
  Fade,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  ThumbDown as ThumbDownIcon,
  ThumbDownOutlined as ThumbDownOutlinedIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface ChatMessageFeedbackProps {
  messageId: string;
  sessionId: string;
  messageContent: string;
  onFeedbackSubmitted?: (rating: number, isPositive: boolean) => void;
}

const ChatMessageFeedback: React.FC<ChatMessageFeedbackProps> = ({
  messageId,
  sessionId,
  messageContent,
  onFeedbackSubmitted,
}) => {
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleQuickFeedback = async (type: 'positive' | 'negative', event: React.MouseEvent<HTMLButtonElement>) => {
    if (submitted) return;
    
    setFeedbackType(type);
    
    // Para feedback positivo, mostrar popover para detalhes
    if (type === 'positive') {
      setAnchorEl(event.currentTarget);
      setRating(5);
    } else {
      // Para feedback negativo, também mostrar popover
      setAnchorEl(event.currentTarget);
      setRating(2);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (!submitted) {
      setFeedbackType(null);
      setRating(null);
      setComment('');
    }
  };

  const handleSubmit = async () => {
    if (!rating) return;
    
    setLoading(true);

    try {
      await api.post('/feedback/', {
        feature: 'chat',
        feedback_type: 'general',
        entity_type: 'chat_message',
        entity_id: messageId,
        rating,
        comment: comment.trim() || null,
        additional_metadata: {
          session_id: sessionId,
          message_preview: messageContent.substring(0, 200),
          quick_feedback: feedbackType,
        },
      });

      setSubmitted(true);
      onFeedbackSubmitted?.(rating, feedbackType === 'positive');
      
      setTimeout(() => {
        setAnchorEl(null);
      }, 1500);
    } catch (err) {
      console.error('Erro ao enviar feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
      {/* Botão positivo */}
      <Tooltip title={submitted ? 'Feedback enviado' : 'Resposta útil'}>
        <span>
          <IconButton
            size="small"
            onClick={(e) => handleQuickFeedback('positive', e)}
            disabled={submitted}
            sx={{
              p: 0.5,
              color: feedbackType === 'positive' ? 'success.main' : 'text.secondary',
              '&:hover': { color: 'success.main', backgroundColor: 'success.light' },
              opacity: submitted && feedbackType !== 'positive' ? 0.3 : 1,
            }}
          >
            {feedbackType === 'positive' ? (
              <ThumbUpIcon fontSize="small" />
            ) : (
              <ThumbUpOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Botão negativo */}
      <Tooltip title={submitted ? 'Feedback enviado' : 'Resposta não útil'}>
        <span>
          <IconButton
            size="small"
            onClick={(e) => handleQuickFeedback('negative', e)}
            disabled={submitted}
            sx={{
              p: 0.5,
              color: feedbackType === 'negative' ? 'error.main' : 'text.secondary',
              '&:hover': { color: 'error.main', backgroundColor: 'error.light' },
              opacity: submitted && feedbackType !== 'negative' ? 0.3 : 1,
            }}
          >
            {feedbackType === 'negative' ? (
              <ThumbDownIcon fontSize="small" />
            ) : (
              <ThumbDownOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Popover para detalhes */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { p: 2, width: 280, borderRadius: 2 },
        }}
      >
        {submitted ? (
          <Fade in>
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="body1" color="success.main" fontWeight="bold">
                ✓ Obrigado pelo feedback!
              </Typography>
            </Box>
          </Fade>
        ) : (
          <>
            <Typography variant="subtitle2" color="primary" fontWeight="bold" mb={1.5}>
              {feedbackType === 'positive' ? 'O que você gostou?' : 'O que pode melhorar?'}
            </Typography>

            {/* Rating detalhado */}
            <Box sx={{ mb: 1.5, textAlign: 'center' }}>
              <Rating
                value={rating}
                onChange={(_, newValue) => setRating(newValue)}
                size="medium"
                emptyIcon={<StarBorderIcon fontSize="inherit" />}
                icon={<StarIcon fontSize="inherit" />}
              />
            </Box>

            {/* Comentário */}
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="Deixe um comentário (opcional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mb: 1.5 }}
              inputProps={{ maxLength: 500 }}
            />

            {/* Botão enviar */}
            <Button
              fullWidth
              variant="contained"
              size="small"
              onClick={handleSubmit}
              disabled={loading || !rating}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
            >
              {loading ? 'Enviando...' : 'Enviar'}
            </Button>
          </>
        )}
      </Popover>
    </Box>
  );
};

export default ChatMessageFeedback;

